import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { batchesAPI, coursesAPI } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import Modal from '../components/Modal';
import SearchableSelect from '../components/Form/SearchableSelect';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import BatchChangeIndicators from '../components/Batch/BatchChangeIndicators';
import BatchTrainersPanel from '../components/Batch/BatchTrainersPanel';
import MessageDisplay from '../components/MessageDisplay';
import useAsyncOperation from '../hooks/useAsyncOperation';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import StaffPageShell from '../components/StaffPageShell';
import { currentEnrollmentCount } from '../domain/batch/batchDeleteUi';
import useTrainingCenters, { centerOptionsWithFallback } from '../hooks/useTrainingCenters';
import {
  BATCH_NAMING_HELP,
  batchesForCourse,
  suggestNextBatchName,
} from '../domain/batch/batchNaming';

const BatchManagementPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightTrainers = searchParams.get('trainers') === '1';
  const { currentTheme } = useTheme();
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const { loading, error, success, execute, setError, setSuccess } = useAsyncOperation();
  const [editingBatch, setEditingBatch] = useState(null);
  const [trainersBatch, setTrainersBatch] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [modeFilter, setModeFilter] = useState('All');
  const [locationFilter, setLocationFilter] = useState('All');
  const { options: catalogCenters } = useTrainingCenters();
  const [formData, setFormData] = useState({
    batchName: '',
    courseId: '',
    startDate: '',
    expectedCompletionDate: '',
    mode: 'Online',
    location: '',
    status: 'Yet to start',
    holdReason: ''
  });

  useEffect(() => {
    fetchBatches();
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const coursesRes = await coursesAPI.getAll();
      
      setCourses(normalizeApiListResponse(coursesRes?.data));
    } catch (err) {
      console.error('Error fetching reference data:', err);
      setCourses([]);
    }
  };

  const fetchBatches = async () => {
    const response = await execute(async () => await batchesAPI.getAll());
    if (response?.data !== undefined) {
      setBatches(normalizeApiListResponse(response.data));
    } else {
      setBatches([]);
    }
  };

  const getSelectedCourse = (courseId) => courses.find((c) => String(c.id) === String(courseId));

  const isBatchModeCompatible = (courseDeliveryMode, batchMode) => {
    if (!courseDeliveryMode) return true;
    if (courseDeliveryMode === 'Both') return true;
    return courseDeliveryMode === batchMode;
  };

  const handleCreate = () => {
    setEditingBatch(null);
    setFormData({
      batchName: '',
      courseId: '',
      startDate: '',
      expectedCompletionDate: '',
      mode: 'Online',
      location: '',
      status: 'Yet to start',
      holdReason: ''
    });
    setShowModal(true);
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setFormData({
      batchName: batch.batchName || '',
      courseId: batch.courseId?.toString() || '',
      startDate: batch.startDate ? batch.startDate.split('T')[0] : '',
      expectedCompletionDate: batch.expectedCompletionDate ? batch.expectedCompletionDate.split('T')[0] : '',
      mode: batch.mode || 'Online',
      location: batch.location || '',
      status: batch.status || 'Ongoing',
      holdReason: batch.holdReason || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedCourse = getSelectedCourse(formData.courseId);
    if (selectedCourse?.deliveryMode && !isBatchModeCompatible(selectedCourse.deliveryMode, formData.mode)) {
      setError(`Mode mismatch: selected course is '${selectedCourse.deliveryMode}' and cannot be assigned to '${formData.mode}' batch.`);
      return;
    }
    if (formData.mode === 'Offline' && !formData.location) {
      setError('Location is required for Offline batches.');
      return;
    }
    
    const response = await execute(async () => {
      if (editingBatch) {
        return await batchesAPI.update(editingBatch.id, formData);
      } else {
        return await batchesAPI.create(formData);
      }
    }, { 
      successMessage: `Batch ${editingBatch ? 'updated' : 'created'} successfully!` 
    });
    
    if (response) {
      setShowModal(false);
      setEditingBatch(null);
      fetchBatches();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    const response = await execute(
      async () => await batchesAPI.delete(deleteConfirm.id),
      { successMessage: 'Batch deleted successfully!' }
    );
    
    if (response) {
      setDeleteConfirm(null);
      fetchBatches();
      setTimeout(() => setSuccess(''), 3000);
    } else if (error?.includes('enrollments')) {
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const deriveStatusFromDates = (startDate, expectedCompletionDate, currentStatus) => {
    if (['On Hold', 'Cancelled'].includes(currentStatus)) return currentStatus;
    if (!startDate) return currentStatus || 'Yet to start';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    if (start > today) return 'Yet to start';
    if (expectedCompletionDate) {
      const end = new Date(expectedCompletionDate);
      end.setHours(0, 0, 0, 0);
      if (end < today) return 'Completed';
    }
    return 'Ongoing';
  };

  const handleDateChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    const newStatus = deriveStatusFromDates(
      field === 'startDate' ? value : updated.startDate,
      field === 'expectedCompletionDate' ? value : updated.expectedCompletionDate,
      updated.status
    );
    setFormData({ ...updated, status: newStatus });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const batchCounts = useMemo(() => {
    const total = batches.length;
    const online = batches.filter((b) => b.mode === 'Online').length;
    const offline = batches.filter((b) => b.mode === 'Offline').length;
    return { total, online, offline };
  }, [batches]);

  const availableLocations = useMemo(
    () =>
      centerOptionsWithFallback(
        catalogCenters,
        batches.filter((b) => b.mode === 'Offline')
      ).map((row) => row.value),
    [batches, catalogCenters]
  );

  const filteredBatches = useMemo(() => {
    return batches.filter((batch) => {
      if (modeFilter !== 'All' && batch.mode !== modeFilter) {
        return false;
      }
      if (locationFilter !== 'All') {
        if (batch.mode !== 'Offline') return false;
        return batch.location === locationFilter;
      }
      return true;
    });
  }, [batches, modeFilter, locationFilter]);

  return (
    <StaffPageShell
      title="Batch Management"
      subtitle="Use Manage trainers on a batch · Grant “Assign trainers to batches” in Access Management"
      wide
    >
        <div className="flex justify-end items-center mb-6">
          <button
            onClick={handleCreate}
            className="px-6 py-2 rounded-lg font-medium theme-button-primary flex items-center gap-2"
          >
            <span>+</span> Create Batch
          </button>
        </div>

        {highlightTrainers ? (
          <div className="mb-4 rounded-lg border border-[var(--staff-navy-mid,#1e4d8c)] bg-[#eef4fb] px-4 py-3 text-sm theme-text">
            <strong>Assign trainers:</strong> use <strong>Manage trainers</strong> on a batch card
            below to link or unlink trainers.
            <button
              type="button"
              className="ml-2 underline font-semibold text-[var(--staff-navy,#0e2443)]"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('trainers');
                setSearchParams(next, { replace: true });
              }}
            >
              Dismiss
            </button>
          </div>
        ) : null}

        <MessageDisplay message={error || success} />

        <div className="mb-6 p-4 rounded-lg theme-card border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
              Total: {batchCounts.total}
            </span>
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              Online: {batchCounts.online}
            </span>
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
              Offline: {batchCounts.offline}
            </span>
            <span className="inline-flex text-sm theme-text-secondary">
              Showing: {filteredBatches.length}
            </span>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-end gap-3">
            <div className="w-full md:w-56">
              <label className="block text-sm font-medium mb-1 theme-text">Filter by Mode</label>
              <SearchableSelect
                value={modeFilter}
                onChange={(e) => {
                  const nextMode = e.target.value;
                  setModeFilter(nextMode);
                  if (nextMode === 'Online') {
                    setLocationFilter('All');
                  }
                }}
                className="w-full"
                required
                options={[
                  { value: 'All', label: 'All' },
                  { value: 'Online', label: 'Online' },
                  { value: 'Offline', label: 'Offline' }
                ]}
              />
            </div>

            <div className="w-full md:w-56">
              <label className="block text-sm font-medium mb-1 theme-text">Filter by Location</label>
              <SearchableSelect
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full"
                disabled={modeFilter === 'Online'}
                required
                options={[{ value: 'All', label: 'All' }, ...availableLocations.map((loc) => ({ value: loc, label: loc }))]}
              />
            </div>

            <div className="w-full md:w-auto md:ml-auto">
              <button
                type="button"
                onClick={() => {
                  setModeFilter('All');
                  setLocationFilter('All');
                }}
                className="w-full px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {loading && !batches.length ? (
          <LoadingSpinner message="Loading batches..." />
        ) : filteredBatches.length === 0 ? (
          <div className="text-center py-12 theme-card rounded-lg">
            <p className="text-lg theme-text-secondary mb-4">No batches found for selected filters</p>
            <button
              onClick={() => {
                setModeFilter('All');
                setLocationFilter('All');
              }}
              className="px-6 py-2 rounded-lg font-medium theme-button-primary"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBatches.map((batch) => (
              <div
                key={batch.id}
                className="p-6 rounded-lg theme-card theme-hover-lift border border-gray-200 dark:border-gray-700"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold mb-2 theme-text">{batch.batchName}</h3>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={batch.status} variant="pill" className="text-xs" />
                      <BatchChangeIndicators batch={batch} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(batch);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit batch"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(batch);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete batch"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium theme-text-secondary">Course:</span>
                    <span className="theme-text">{batch.course?.courseName || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium theme-text-secondary">Mode:</span>
                    <span className="theme-text">{batch.mode}</span>
                    {batch.mode === 'Offline' && batch.location && (
                      <>
                        <span className="theme-text-secondary">|</span>
                        <span className="theme-text">{batch.location}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium theme-text-secondary">Start Date:</span>
                    <span className="theme-text">{formatDate(batch.startDate)}</span>
                  </div>
                  {batch.expectedCompletionDate && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium theme-text-secondary">Completion:</span>
                      <span className="theme-text">{formatDate(batch.expectedCompletionDate)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium theme-text-secondary">Students:</span>
                    <span className="font-semibold theme-text">{batch.enrollments?.length || 0}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="font-medium theme-text-secondary shrink-0">Trainers:</span>
                    <span className="theme-text">
                      {batch.trainers?.length
                        ? batch.trainers
                            .map(
                              (a) =>
                                a.trainer?.fullName ||
                                `${a.trainer?.firstName || ''} ${a.trainer?.lastName || ''}`.trim() ||
                                a.trainer?.email
                            )
                            .filter(Boolean)
                            .join(', ')
                        : 'None linked'}
                    </span>
                  </div>
                </div>

                {batch.holdReason && (
                  <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Hold Reason:</strong> {batch.holdReason}
                    </p>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setTrainersBatch(batch)}
                    className="px-3 py-2 rounded-lg font-medium theme-button-primary text-sm"
                  >
                    Manage trainers
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/batches/${batch.id}`)}
                    className="px-3 py-2 rounded-lg font-medium theme-button-secondary text-sm"
                  >
                    View details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Modal
          isOpen={Boolean(trainersBatch)}
          onClose={() => setTrainersBatch(null)}
          title={
            trainersBatch
              ? `Trainers · ${trainersBatch.batchName}`
              : 'Trainers'
          }
          size="md"
        >
          {trainersBatch ? (
            <BatchTrainersPanel
              batchId={trainersBatch.id}
              initialTrainers={trainersBatch.trainers || []}
              compact
              onChanged={(trainers) => {
                setBatches((prev) =>
                  prev.map((b) =>
                    b.id === trainersBatch.id ? { ...b, trainers } : b
                  )
                );
                setTrainersBatch((prev) =>
                  prev ? { ...prev, trainers } : prev
                );
              }}
            />
          ) : null}
        </Modal>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingBatch(null);
          }}
          title={editingBatch ? 'Edit Batch' : 'Create New Batch'}
          size="md"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block theme-text mb-1 font-medium">Batch Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.batchName}
                      onChange={(e) => setFormData({ ...formData, batchName: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg theme-input"
                      placeholder="e.g., Batch-1"
                    />
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      {BATCH_NAMING_HELP}
                      {formData.courseId && !editingBatch
                        ? ` Next: ${suggestNextBatchName(batchesForCourse(batches, formData.courseId))}.`
                        : ''}
                    </p>
                  </div>

                  <div>
                    <label className="block theme-text mb-1 font-medium">Course *</label>
                    <SearchableSelect
                      required
                      value={formData.courseId}
                      onChange={(e) => {
                        const selected = getSelectedCourse(e.target.value);
                        const forcedMode = selected?.deliveryMode && selected.deliveryMode !== 'Both' ? selected.deliveryMode : formData.mode;
                        setFormData({
                          ...formData,
                          courseId: e.target.value,
                          mode: forcedMode,
                          batchName: editingBatch
                            ? formData.batchName
                            : suggestNextBatchName(batchesForCourse(batches, e.target.value)),
                          ...(forcedMode === 'Online' ? { location: '' } : {})
                        });
                        setError('');
                      }}
                      className="w-full"
                      placeholder={courses.length === 0 ? 'Loading courses...' : 'Select Course'}
                      options={courses}
                      getOptionValue={(course) => course.id}
                      getOptionLabel={(course) =>
                        `${course.courseName || course.name || `Course ${course.id}`}` +
                        (course.category ? ` (${course.category.categoryName || course.category.name || ''})` : '') +
                        (course.courseCode ? ` - ${course.courseCode}` : '') +
                        (course.deliveryMode ? ` [${course.deliveryMode}]` : '')
                      }
                    />
                    {courses.length === 0 && (
                      <p className="text-xs text-yellow-500 mt-1">
                        ⚠️ No courses found. Please create courses first.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block theme-text mb-1 font-medium">Status *</label>
                    <SearchableSelect
                      required
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full"
                      options={[
                        { value: 'Yet to start', label: 'Yet to start' },
                        { value: 'Ongoing', label: 'Ongoing' },
                        { value: 'Completed', label: 'Completed' },
                        { value: 'On Hold', label: 'On Hold' },
                        { value: 'Cancelled', label: 'Cancelled' }
                      ]}
                    />
                    <p className="text-xs theme-text-secondary mt-1">Auto-synced with dates. Manual override for On Hold / Cancelled.</p>
                  </div>

                  <div>
                    <label className="block theme-text mb-1 font-medium">Start Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.startDate}
                      onChange={(e) => handleDateChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg theme-input"
                    />
                  </div>

                  <div>
                    <label className="block theme-text mb-1 font-medium">Expected Completion Date</label>
                    <input
                      type="date"
                      value={formData.expectedCompletionDate}
                      onChange={(e) => handleDateChange('expectedCompletionDate', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg theme-input"
                    />
                  </div>

                  <div>
                    <label className="block theme-text mb-1 font-medium">Mode *</label>
                    <SearchableSelect
                      required
                      value={formData.mode}
                      onChange={(e) => {
                        const nextMode = e.target.value;
                        const selectedCourse = getSelectedCourse(formData.courseId);
                        if (selectedCourse?.deliveryMode && !isBatchModeCompatible(selectedCourse.deliveryMode, nextMode)) {
                          setError(`Mode mismatch: course '${selectedCourse.courseName}' supports '${selectedCourse.deliveryMode}' only.`);
                          return;
                        }
                        setFormData({ ...formData, mode: nextMode, ...(nextMode === 'Online' ? { location: '' } : {}) });
                        setError('');
                      }}
                      className="w-full"
                      options={[
                        { value: 'Online', label: 'Online' },
                        { value: 'Offline', label: 'Offline' }
                      ]}
                    />
                    {(() => {
                      const selectedCourse = getSelectedCourse(formData.courseId);
                      if (!selectedCourse?.deliveryMode) return null;
                      return (
                        <p className="text-xs theme-text-secondary mt-1">
                          Course delivery: {selectedCourse.deliveryMode}
                        </p>
                      );
                    })()}
                  </div>

                  {formData.mode === 'Offline' && (
                  <div>
                    <label className="block theme-text mb-1 font-medium">Center Location *</label>
                    <SearchableSelect
                      required
                      creatable
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full"
                      placeholder="Select Location of Center"
                      options={centerOptionsWithFallback(catalogCenters, batches, [formData.location])}
                    />
                    <p className="text-xs mt-1 theme-text-secondary">
                      Shared list from Training centers. Typed values are added there.
                    </p>
                  </div>
                  )}
                </div>

                {formData.status === 'On Hold' && (
                  <div>
                    <label className="block theme-text mb-1 font-medium">Hold Reason</label>
                    <textarea
                      value={formData.holdReason}
                      onChange={(e) => setFormData({ ...formData, holdReason: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg theme-input"
                      rows="3"
                      placeholder="Enter reason for putting batch on hold..."
                    />
                  </div>
                )}

                <div className="flex space-x-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-primary disabled:opacity-50"
                  >
                    {loading ? (editingBatch ? 'Updating...' : 'Creating...') : (editingBatch ? 'Update' : 'Create')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingBatch(null);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          title="Delete Batch"
          size="sm"
        >
          <p className="theme-text-secondary mb-4">
            Are you sure you want to delete <strong>{deleteConfirm?.batchName}</strong>?
          </p>
          {currentEnrollmentCount(deleteConfirm) > 0 ? (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">
                <strong>Warning:</strong> This batch has {currentEnrollmentCount(deleteConfirm)} current
                student enrollment(s). Move or remove those students before deleting the batch.
              </p>
            </div>
          ) : (deleteConfirm?.enrollments?.length || 0) > 0 ? (
            <p className="theme-text-secondary mb-4 text-sm">
              Students previously moved to other batches will be cleared from this batch so the name
              can be reused.
            </p>
          ) : null}
          <div className="flex space-x-4">
            <button
              onClick={handleDelete}
              disabled={loading || currentEnrollmentCount(deleteConfirm) > 0}
              className="flex-1 px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
            >
              Cancel
            </button>
          </div>
        </Modal>
    </StaffPageShell>
  );
};

export default BatchManagementPage;
